import { supabase } from '../../config/supabase';
import logger from '../../utils/logger';
import { User as SupabaseUser, Session, AuthError } from '@supabase/supabase-js';
import { User, UserUpdateInput } from '../../types/user';
import { PostgrestError } from '@supabase/supabase-js';
import { resolveAppUrl } from '../../utils/resolveAppUrl';

/**
 * Interface pour les données d'inscription
 */
export interface SignUpData {
  email: string;
  password: string;
  name: string;
  firstName: string;
  agencyName: string;
  agencyAddress: string;
  photoFile: File | null;
}

/**
 * Service d'authentification utilisant Supabase
 */
class AuthService {
  /**
   * Inscrit un nouvel utilisateur avec toutes les informations nécessaires
   * @param data Données d'inscription (email, password, nom, prénom, agence, etc.)
   * @returns Promise avec les données utilisateur ou une erreur
   */
  async signUp(data: SignUpData): Promise<{ user: SupabaseUser | null; session: Session | null; error: AuthError | null }> {
    try {
      logger.info(`Tentative d'inscription pour l'email: ${data.email}`);
      
      // Préparer les données en snake_case pour le trigger DB
      const metaData = {
        first_name: data.firstName?.trim(),
        name: data.name?.trim(),
        agency_name: data.agencyName?.trim(),
        agency_address: data.agencyAddress?.trim()
      };
      
      // Utiliser l'utilitaire resolveAppUrl pour déterminer l'URL de l'application
      const appUrl = resolveAppUrl();
      const emailRedirectTo = `${appUrl}/auth/callback`;
      
      // Logs conditionnels pour le développement uniquement
      if (process.env.NODE_ENV === 'development') {
        console.log('signup appUrl =', appUrl);
        console.log('emailRedirectTo =', emailRedirectTo);
      }
      
      // Log des données envoyées pour vérification (développement uniquement)
      if (process.env.NODE_ENV === 'development') {
        logger.debug('Données envoyées à Supabase Auth:', {
          metadata: metaData,
          redirectTo: emailRedirectTo
        });
      }
      
      // Créer l'utilisateur dans auth.users avec toutes les données nécessaires dans options.data
      // Le trigger Supabase s'occupera de créer l'agence et le profil utilisateur
      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: emailRedirectTo,
          data: metaData
        }
      });

      if (error) {
        logger.error('Erreur lors de l\'inscription:', error);
        return { user: null, session: null, error };
      }

      // Si l'inscription est réussie, on ne fait rien de plus
      // Le trigger DB s'occupe de créer l'agence et l'utilisateur
      logger.info('Inscription réussie, vérification par email envoyée');
      logger.info('L\'agence et le profil utilisateur seront créés par le trigger DB');
      
      // La gestion de la photo sera faite après confirmation de l'email et connexion
      if (data.photoFile) {
        logger.info('Photo de profil fournie, elle sera gérée après confirmation de l\'email');
      }
      
      return { user: authData.user, session: authData.session, error: null };
    } catch (error) {
      logger.error('Exception lors de l\'inscription:', error);
      return { user: null, session: null, error: error as AuthError };
    }
  }

  /**
   * Connecte un utilisateur avec email et mot de passe
   * @param email Email de l'utilisateur
   * @param password Mot de passe de l'utilisateur
   * @returns Promise avec les données utilisateur ou une erreur
   */
  async signIn(email: string, password: string): Promise<{ user: SupabaseUser | null; session: Session | null; error: AuthError | null }> {
    try {
      logger.info(`Tentative de connexion pour l'email: ${email}`);
      
      // Utiliser signInWithPassword pour la connexion
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logger.error('Erreur lors de la connexion:', error);
        return { user: null, session: null, error };
      }

      logger.info('Connexion réussie');
      return { user: data.user, session: data.session, error: null };
    } catch (error) {
      logger.error('Exception lors de la connexion:', error);
      return { user: null, session: null, error: error as AuthError };
    }
  }

  /**
   * Déconnecte l'utilisateur actuel
   * @returns Promise avec le statut de la déconnexion
   */
  async signOut(): Promise<{ error: AuthError | null }> {
    try {
      logger.info('Tentative de déconnexion');
      const { error } = await supabase.auth.signOut();

      if (error) {
        logger.error('Erreur lors de la déconnexion:', error);
        return { error };
      }

      logger.info('Déconnexion réussie');
      return { error: null };
    } catch (error) {
      logger.error('Exception lors de la déconnexion:', error);
      return { error: error as AuthError };
    }
  }

  /**
   * Récupère la session utilisateur actuelle
   * @returns Promise avec les données de session ou null
   */
  async getSession(): Promise<{ session: Session | null; error: AuthError | null }> {
    try {
      logger.debug('Récupération de la session utilisateur');
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        logger.error('Erreur lors de la récupération de la session:', error);
        return { session: null, error };
      }

      if (data.session) {
        logger.debug('Session trouvée:', data.session.user.email);
      } else {
        logger.debug('Aucune session active trouvée');
      }

      return { session: data.session, error: null };
    } catch (error) {
      logger.error('Exception lors de la récupération de la session:', error);
      return { session: null, error: error as AuthError };
    }
  }

  /**
   * Récupère l'utilisateur actuel
   * @returns Promise avec les données utilisateur ou null
   */
  async getCurrentUser(): Promise<{ user: SupabaseUser | null; error: AuthError | null }> {
    try {
      logger.debug('Récupération de l\'utilisateur actuel');
      const { data, error } = await supabase.auth.getUser();

      if (error) {
        logger.error('Erreur lors de la récupération de l\'utilisateur:', error);
        return { user: null, error };
      }

      if (data.user) {
        logger.debug('Utilisateur trouvé:', data.user.email);
      } else {
        logger.debug('Aucun utilisateur connecté trouvé');
      }

      return { user: data.user, error: null };
    } catch (error) {
      logger.error('Exception lors de la récupération de l\'utilisateur:', error);
      return { user: null, error: error as AuthError };
    }
  }

  /**
   * Récupère le profil utilisateur complet avec les informations de l'agence
   * @param userId ID de l'utilisateur
   * @returns Promise avec le profil utilisateur ou null
   */
  async getUserProfile(userId: string): Promise<{ userProfile: User | null; error: AuthError | PostgrestError | null }> {
    try {
      logger.debug(`Récupération du profil utilisateur pour l'ID: ${userId}`);
      
      const { data, error } = await supabase
        .from('users')
        .select('*, agencies(*)')
        .eq('id', userId)
        .single();

      if (error) {
        logger.error('Erreur lors de la récupération du profil utilisateur:', error);
        return { userProfile: null, error };
      }

      if (!data) {
        logger.error(`Aucun profil trouvé pour l'utilisateur ID: ${userId}`);
        return { userProfile: null, error: null };
      }

      // Convertir les données brutes en objet User
      const userProfile: User = {
        id: data.id,
        agency_id: data.agency_id,
        email: data.email,
        name: data.name,
        first_name: data.first_name,
        photo_url: data.photo_url,
        color: data.color,
        role: data.role,
        is_owner: data.is_owner,
        created_at: new Date(data.created_at),
        agency: data.agencies
      };

      return { userProfile, error: null };
    } catch (error) {
      logger.error('Exception lors de la récupération du profil utilisateur:', error);
      return { userProfile: null, error: error as AuthError };
    }
  }

  /**
   * Récupère les informations de l'agence associée à un utilisateur
   * @param agencyId ID de l'agence
   * @returns Promise avec le nom et l'adresse de l'agence ou null
   */
  async getAgencyName(agencyId: string): Promise<{ agencyName: string | null; agencyAddress: string | null; error: AuthError | PostgrestError | null }> {
    try {
      logger.debug(`Récupération des informations de l'agence ID: ${agencyId}`);
      
      const { data, error } = await supabase
        .from('agencies')
        .select('name, address')
        .eq('id', agencyId)
        .single();

      if (error) {
        logger.error('Erreur lors de la récupération des informations de l\'agence:', error);
        return { agencyName: null, agencyAddress: null, error };
      }

      if (!data) {
        logger.error(`Aucune agence trouvée pour l'ID: ${agencyId}`);
        return { agencyName: null, agencyAddress: null, error: null };
      }

      // Afficher les données brutes pour déboguer (développement uniquement)
      if (process.env.NODE_ENV === 'development') {
        console.log('Données brutes de l\'agence:', data);
        console.log('Adresse de l\'agence:', data.address);
      }

      return { agencyName: data.name, agencyAddress: data.address, error: null };
    } catch (error) {
      logger.error('Exception lors de la récupération des informations de l\'agence:', error);
      return { agencyName: null, agencyAddress: null, error: error as AuthError };
    }
  }

  /**
   * Met à jour le profil utilisateur
   * @param userId ID de l'utilisateur
   * @param userData Données utilisateur à mettre à jour
   * @returns Promise avec le profil utilisateur mis à jour ou une erreur
   */
  async updateUserProfile(userId: string, userData: UserUpdateInput): Promise<{ userProfile: User | null; error: AuthError | PostgrestError | null }> {
    try {
      logger.debug(`Mise à jour du profil utilisateur pour l'ID: ${userId}`);
      
      const { data, error } = await supabase
        .from('users')
        .update(userData)
        .eq('id', userId)
        .select('*, agencies(*)')
        .single();

      if (error) {
        logger.error('Erreur lors de la mise à jour du profil utilisateur:', error);
        return { userProfile: null, error };
      }

      if (!data) {
        logger.error(`Aucun profil trouvé pour l'utilisateur ID: ${userId}`);
        return { userProfile: null, error: null };
      }

      // Convertir les données brutes en objet User
      const userProfile: User = {
        id: data.id,
        agency_id: data.agency_id,
        email: data.email,
        name: data.name,
        first_name: data.first_name,
        photo_url: data.photo_url,
        color: data.color,
        role: data.role,
        is_owner: data.is_owner,
        created_at: new Date(data.created_at),
        agency: data.agencies
      };

      return { userProfile, error: null };
    } catch (error) {
      logger.error('Exception lors de la mise à jour du profil utilisateur:', error);
      return { userProfile: null, error: error as AuthError };
    }
  }

  /**
   * Réinitialise le mot de passe d'un utilisateur
   * @param email Email de l'utilisateur
   * @returns Promise avec le statut de la réinitialisation
   */
  async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    try {
      logger.info(`Tentative de réinitialisation du mot de passe pour l'email: ${email}`);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        logger.error('Erreur lors de la réinitialisation du mot de passe:', error);
        return { error };
      }

      logger.info('Email de réinitialisation envoyé');
      return { error: null };
    } catch (error) {
      logger.error('Exception lors de la réinitialisation du mot de passe:', error);
      return { error: error as AuthError };
    }
  }

  /**
   * Met à jour le mot de passe d'un utilisateur
   * @param newPassword Nouveau mot de passe
   * @returns Promise avec le statut de la mise à jour
   */
  async updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
    try {
      logger.info('Tentative de mise à jour du mot de passe');
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        logger.error('Erreur lors de la mise à jour du mot de passe:', error);
        return { error };
      }

      logger.info('Mot de passe mis à jour avec succès');
      return { error: null };
    } catch (error) {
      logger.error('Exception lors de la mise à jour du mot de passe:', error);
      return { error: error as AuthError };
    }
  }

  /**
   * Crée un nouvel utilisateur dans l'agence (par le propriétaire)
   * @param agencyId ID de l'agence
   * @param userData Données du nouvel utilisateur
   * @returns Promise avec le statut de la création
   */
  async createAgencyUser(agencyId: string, userData: UserUpdateInput & { email: string; password: string }): Promise<{ user: User | null; error: AuthError | PostgrestError | null }> {
    try {
      logger.info(`Tentative de création d'un nouvel utilisateur pour l'agence ID: ${agencyId}`);
      
      // 1. Vérifier si l'utilisateur actuel est propriétaire de l'agence
      const { user: currentUser, error: getCurrentUserError } = await this.getCurrentUser();
      
      if (getCurrentUserError) {
        logger.error('Erreur lors de la récupération de l\'utilisateur actuel:', getCurrentUserError);
        return { user: null, error: getCurrentUserError };
      }
      
      if (!currentUser) {
        const noUserError = new Error('Aucun utilisateur connecté') as PostgrestError;
        logger.error('Erreur: Aucun utilisateur connecté');
        return { user: null, error: noUserError };
      }
      
      const { isOwner, error: ownerCheckError } = await this.isAgencyOwner(currentUser.id);
      
      if (ownerCheckError) {
        logger.error('Erreur lors de la vérification du statut de propriétaire:', ownerCheckError);
        return { user: null, error: ownerCheckError };
      }
      
      if (!isOwner) {
        const notOwnerError = new Error('Seul le propriétaire peut créer de nouveaux utilisateurs') as PostgrestError;
        logger.error('Erreur: Utilisateur non propriétaire');
        return { user: null, error: notOwnerError };
      }
      
      // 2. Créer l'utilisateur dans auth.users
      const { data: authData, error: authError } = await supabase.auth.admin.createUser({
        email: userData.email,
        password: userData.password,
        email_confirm: true
      });
      
      if (authError) {
        logger.error('Erreur lors de la création de l\'utilisateur dans auth:', authError);
        return { user: null, error: authError };
      }
      
      if (!authData.user) {
        const noUserError = new Error('Impossible de créer l\'utilisateur dans auth') as PostgrestError;
        logger.error('Erreur: Utilisateur non créé dans auth');
        return { user: null, error: noUserError };
      }
      
      // 3. Créer le profil utilisateur avec l'agence attribuée
      const { data: profileData, error: profileError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          email: userData.email,
          name: userData.name || '',
          first_name: userData.first_name || '',
          photo_url: userData.photo_url || null,
          agency_id: agencyId,
          is_owner: false, // Utilisateur créé par le propriétaire, donc non propriétaire
          role: userData.role || 'user',
          created_at: new Date().toISOString()
        })
        .select('*, agencies(*)')
        .single();
      
      if (profileError) {
        logger.error('Erreur lors de la création du profil utilisateur:', profileError);
        return { user: null, error: profileError };
      }
      
      // Convertir les données brutes en objet User
      const user: User = {
        id: profileData.id,
        agency_id: profileData.agency_id,
        email: profileData.email,
        name: profileData.name,
        first_name: profileData.first_name,
        photo_url: profileData.photo_url,
        color: profileData.color,
        role: profileData.role,
        is_owner: profileData.is_owner,
        created_at: new Date(profileData.created_at),
        agency: profileData.agencies
      };
      
      logger.info('Nouvel utilisateur créé avec succès');
      return { user, error: null };
    } catch (error) {
      logger.error('Exception lors de la création d\'un nouvel utilisateur:', error);
      return { user: null, error: error as PostgrestError };
    }
  }

  /**
   * Supprime un utilisateur de l'agence (par le propriétaire)
   * @param userId ID de l'utilisateur à supprimer
   * @returns Promise avec le statut de la suppression
   */
  async deleteAgencyUser(userId: string): Promise<{ success: boolean; error: AuthError | PostgrestError | null }> {
    try {
      logger.info(`Tentative de suppression de l'utilisateur ID: ${userId}`);
      
      // 1. Vérifier si l'utilisateur actuel est propriétaire de l'agence
      const { user: currentUser, error: getCurrentUserError } = await this.getCurrentUser();
      
      if (getCurrentUserError) {
        logger.error('Erreur lors de la récupération de l\'utilisateur actuel:', getCurrentUserError);
        return { success: false, error: getCurrentUserError };
      }
      
      if (!currentUser) {
        const noUserError = new Error('Aucun utilisateur connecté') as PostgrestError;
        logger.error('Erreur: Aucun utilisateur connecté');
        return { success: false, error: noUserError };
      }
      
      const { isOwner, error: ownerCheckError } = await this.isAgencyOwner(currentUser.id);
      
      if (ownerCheckError) {
        logger.error('Erreur lors de la vérification du statut de propriétaire:', ownerCheckError);
        return { success: false, error: ownerCheckError };
      }
      
      if (!isOwner) {
        const notOwnerError = new Error('Seul le propriétaire peut supprimer des utilisateurs') as PostgrestError;
        logger.error('Erreur: Utilisateur non propriétaire');
        return { success: false, error: notOwnerError };
      }
      
      // 2. Exécuter la procédure SQL pour supprimer l'utilisateur
      const { error: deleteError } = await supabase.rpc('delete_user', { user_id: userId });
      
      if (deleteError) {
        logger.error('Erreur lors de la suppression de l\'utilisateur:', deleteError);
        return { success: false, error: deleteError };
      }
      
      logger.info('Utilisateur supprimé avec succès');
      return { success: true, error: null };
    } catch (error) {
      logger.error('Exception lors de la suppression de l\'utilisateur:', error);
      return { success: false, error: error as PostgrestError };
    }
  }

  /**
   * Supprime une agence et tous ses utilisateurs (par le propriétaire)
   * @param agencyId ID de l'agence à supprimer
   * @returns Promise avec le statut de la suppression
   */
  async deleteAgency(agencyId: string): Promise<{ success: boolean; error: AuthError | PostgrestError | null }> {
    try {
      logger.info(`Tentative de suppression de l'agence ID: ${agencyId}`);
      
      // 1. Vérifier si l'utilisateur actuel est propriétaire de l'agence
      const { user: currentUser, error: getCurrentUserError } = await this.getCurrentUser();
      
      if (getCurrentUserError) {
        logger.error('Erreur lors de la récupération de l\'utilisateur actuel:', getCurrentUserError);
        return { success: false, error: getCurrentUserError };
      }
      
      if (!currentUser) {
        const noUserError = new Error('Aucun utilisateur connecté') as PostgrestError;
        logger.error('Erreur: Aucun utilisateur connecté');
        return { success: false, error: noUserError };
      }
      
      const { isOwner, error: ownerCheckError } = await this.isAgencyOwner(currentUser.id);
      
      if (ownerCheckError) {
        logger.error('Erreur lors de la vérification du statut de propriétaire:', ownerCheckError);
        return { success: false, error: ownerCheckError };
      }
      
      if (!isOwner) {
        const notOwnerError = new Error('Seul le propriétaire peut supprimer une agence') as PostgrestError;
        logger.error('Erreur: Utilisateur non propriétaire');
        return { success: false, error: notOwnerError };
      }
      
      // 2. Exécuter la procédure SQL pour supprimer l'agence
      const { error: deleteError } = await supabase.rpc('delete_agency', { agency_id: agencyId });
      
      if (deleteError) {
        logger.error('Erreur lors de la suppression de l\'agence:', deleteError);
        return { success: false, error: deleteError };
      }
      
      logger.info('Agence supprimée avec succès');
      return { success: true, error: null };
    } catch (error) {
      logger.error('Exception lors de la suppression de l\'agence:', error);
      return { success: false, error: error as PostgrestError };
    }
  }

  /**
   * Vérifie si l'utilisateur est propriétaire de l'agence
   * @param userId ID de l'utilisateur
   * @returns Promise avec le statut de propriétaire
   */
  async isAgencyOwner(userId: string): Promise<{ isOwner: boolean; error: AuthError | PostgrestError | null }> {
    try {
      logger.debug(`Vérification du statut de propriétaire pour l'utilisateur ID: ${userId}`);
      
      const { data, error } = await supabase
        .from('users')
        .select('is_owner')
        .eq('id', userId)
        .single();
      
      if (error) {
        logger.error('Erreur lors de la vérification du statut de propriétaire:', error);
        return { isOwner: false, error };
      }
      
      if (!data) {
        logger.error(`Aucun utilisateur trouvé pour l'ID: ${userId}`);
        return { isOwner: false, error: null };
      }
      
      logger.debug(`Statut de propriétaire: ${data.is_owner}`);
      return { isOwner: data.is_owner, error: null };
    } catch (error) {
      logger.error('Exception lors de la vérification du statut de propriétaire:', error);
      return { isOwner: false, error: error as PostgrestError };
    }
  }

  /**
   * Récupère tous les utilisateurs d'une agence
   * @param agencyId ID de l'agence
   * @returns Promise avec la liste des utilisateurs
   */
  async getAgencyUsers(agencyId: string): Promise<{ users: User[]; error: AuthError | PostgrestError | null }> {
    try {
      logger.debug(`Récupération des utilisateurs pour l'agence ID: ${agencyId}`);
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('agency_id', agencyId);
      
      if (error) {
        logger.error('Erreur lors de la récupération des utilisateurs de l\'agence:', error);
        return { users: [], error };
      }
      
      if (!data || data.length === 0) {
        logger.debug(`Aucun utilisateur trouvé pour l'agence ID: ${agencyId}`);
        return { users: [], error: null };
      }
      
      // Convertir les données brutes en objets User
      const users: User[] = data.map(item => ({
        id: item.id,
        agency_id: item.agency_id,
        email: item.email,
        name: item.name,
        first_name: item.first_name,
        photo_url: item.photo_url,
        color: item.color,
        role: item.role,
        is_owner: item.is_owner,
        created_at: new Date(item.created_at)
      }));
      
      logger.debug(`${users.length} utilisateurs récupérés pour l'agence ID: ${agencyId}`);
      return { users, error: null };
    } catch (error) {
      logger.error('Exception lors de la récupération des utilisateurs de l\'agence:', error);
      return { users: [], error: error as PostgrestError };
    }
  }

  /**
   * S'abonne aux changements d'état d'authentification
   * @param callback Fonction à appeler lors d'un changement d'état
   * @returns Fonction pour se désabonner
   */
  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      callback(event, session);
    });
    
    return () => {
      data.subscription.unsubscribe();
    };
  }
}

// Exporter une instance unique du service
export const authService = new AuthService();


